/**
 * Entity Framework Hooks for Consumer Portal
 *
 * Hooks for dynamic entity record management.
 * These hooks connect to the Entity Framework Service via tRPC.
 *
 * @module hooks/useEntities
 * @see Issue #65 - Complete Service 29 migration and unify portal architecture
 */

import type { FieldDefinition, FieldType } from '@ops-matrix/design-system-ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { trpc } from '../lib/trpc/client';

// ============================================================================
// Types
// ============================================================================

export interface EntityDefinition {
  id: string;
  name: string;
  pluralName: string;
  description?: string;
  icon?: string;
  fields: FieldDefinition[];
  allowCreate: boolean;
  allowEdit: boolean;
  allowDelete: boolean;
}

export interface EntityRecord {
  id: string;
  entityId: string;
  data: Record<string, unknown>;
  status?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

export type FilterOperator =
  | 'eq'
  | 'ne'
  | 'gt'
  | 'lt'
  | 'gte'
  | 'lte'
  | 'contains'
  | 'startsWith'
  | 'endsWith'
  | 'in'
  | 'notIn'
  | 'isNull'
  | 'isNotNull';

export interface EntityFilter {
  field: string;
  operator: FilterOperator;
  value: unknown;
}

export interface EntityRecordListOptions {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  filters?: EntityFilter[];
}

export interface EntityRecordListResponse {
  records: EntityRecord[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ============================================================================
// API Response Transformers
// ============================================================================

/**
 * Transform API entity response to EntityDefinition format
 */
function transformEntityResponse(apiEntity: Record<string, unknown>): EntityDefinition {
  const fields = (apiEntity.fields as Array<Record<string, unknown>>) ?? [];

  return {
    id: apiEntity.id as string,
    name: (apiEntity.label as string) || (apiEntity.name as string),
    pluralName: (apiEntity.plural_label as string) || (apiEntity.plural_name as string),
    description: apiEntity.description as string | undefined,
    icon: apiEntity.icon as string | undefined,
    allowCreate: (apiEntity.allow_create as boolean) ?? true,
    allowEdit: (apiEntity.allow_edit as boolean) ?? true,
    allowDelete: (apiEntity.allow_delete as boolean) ?? false,
    fields: fields.map(
      (f): FieldDefinition => ({
        name: f.name as string,
        label: f.label as string,
        fieldType: (f.field_type as string as FieldType) ?? 'TEXT',
        required: (f.required as boolean) ?? false,
        options: f.options as Array<{ value: string; label: string }> | undefined,
        placeholder: f.placeholder as string | undefined,
        helpText: f.help_text as string | undefined,
      }),
    ),
  };
}

/**
 * Transform API record response to EntityRecord format
 */
function transformRecordResponse(apiRecord: Record<string, unknown>, entityId: string): EntityRecord {
  const recordData =
    typeof apiRecord.data === 'string'
      ? JSON.parse(apiRecord.data as string)
      : (apiRecord.data as Record<string, unknown>);

  return {
    id: apiRecord.id as string,
    entityId,
    data: recordData ?? {},
    status: apiRecord.status as string | undefined,
    createdAt: apiRecord.created_at as string,
    updatedAt: apiRecord.updated_at as string,
    createdBy: apiRecord.created_by as string | undefined,
  };
}

// ============================================================================
// Entity Definition Hooks
// ============================================================================

/**
 * Get all entity definitions available to the consumer
 */
export function useConsumerEntities() {
  const query = trpc.entities.list.useQuery({ page: 1, limit: 100, includeFields: true, activeOnly: true });

  return {
    ...query,
    data: query.data
      ? (query.data.data || []).map((e: Record<string, unknown>) => transformEntityResponse(e))
      : undefined,
  };
}

/**
 * Get a single entity definition by ID
 */
export function useConsumerEntity(entityId: string | null) {
  const query = trpc.entities.get.useQuery(
    { id: entityId ?? '', includeFields: true },
    { enabled: entityId !== null && entityId !== '' },
  );

  return {
    ...query,
    data: query.data ? transformEntityResponse(query.data as Record<string, unknown>) : undefined,
  };
}

/**
 * Get field definitions for an entity
 */
export function useEntityFields(entityId: string | null) {
  const { data: entity, ...rest } = useConsumerEntity(entityId);
  return {
    ...rest,
    data: entity?.fields ?? [],
  };
}

// ============================================================================
// Entity Record Hooks
// ============================================================================

/**
 * Get records for an entity type
 */
export function useConsumerRecords(entityId: string | null, options?: EntityRecordListOptions) {
  const page = options?.page ?? 1;
  const limit = options?.limit ?? 20;

  const query = trpc.entities.records.list.useQuery(
    {
      entityId: entityId ?? '',
      page,
      limit,
      sort: options?.sortBy ? { field: options.sortBy, direction: options.sortOrder ?? 'desc' } : undefined,
      filters: options?.filters,
    },
    { enabled: entityId !== null && entityId !== '' },
  );

  return {
    ...query,
    data: query.data
      ? {
          records: (query.data.data || []).map((r: Record<string, unknown>) =>
            transformRecordResponse(r, entityId ?? ''),
          ),
          total: query.data.total,
          page: query.data.page,
          limit: query.data.limit,
          totalPages: query.data.totalPages,
        }
      : undefined,
  };
}

/**
 * Get a single record by ID
 */
export function useConsumerRecord(entityId: string | null, recordId: string | null) {
  const query = trpc.entities.records.get.useQuery(
    { entityId: entityId ?? '', recordId: recordId ?? '' },
    {
      enabled: entityId !== null && entityId !== '' && recordId !== null && recordId !== '',
    },
  );

  return {
    ...query,
    data: query.data ? transformRecordResponse(query.data as Record<string, unknown>, entityId ?? '') : undefined,
  };
}

/**
 * Create a new record
 */
export function useCreateRecord() {
  const utils = trpc.useUtils();

  return trpc.entities.records.create.useMutation({
    onSuccess: () => {
      // Invalidate without input to refresh all paginated/filtered variants
      void utils.entities.records.list.invalidate();
    },
  });
}

/**
 * Update an existing record
 */
export function useUpdateRecord() {
  const utils = trpc.useUtils();

  return trpc.entities.records.update.useMutation({
    onSuccess: (_data, variables) => {
      // Invalidate without input to refresh all paginated/filtered variants
      void utils.entities.records.list.invalidate();
      void utils.entities.records.get.invalidate({ entityId: variables.entityId, recordId: variables.recordId });
    },
  });
}

/**
 * Save a draft record (local storage fallback for offline support)
 */
export function useSaveDraft() {
  return useMutation({
    mutationFn: async ({
      entityId,
      data,
      draftId,
    }: {
      entityId: string;
      data: Record<string, unknown>;
      draftId?: string;
    }) => {
      const id = draftId ?? `draft-${entityId}-${Date.now()}`;
      const draft = {
        id,
        entityId,
        data,
        savedAt: new Date().toISOString(),
      };

      // Save to localStorage for offline support
      localStorage.setItem(`consumer-draft-${id}`, JSON.stringify(draft));

      return draft;
    },
  });
}

/**
 * Load a draft record
 */
export function useLoadDraft(draftId: string | null) {
  return useQuery({
    queryKey: ['consumer', 'drafts', draftId],
    queryFn: async () => {
      if (!draftId) return null;
      const stored = localStorage.getItem(`consumer-draft-${draftId}`);
      if (!stored) return null;
      return JSON.parse(stored) as {
        id: string;
        entityId: string;
        data: Record<string, unknown>;
        savedAt: string;
      };
    },
    enabled: draftId !== null,
  });
}

/**
 * Delete a draft record
 */
export function useDeleteDraft() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (draftId: string) => {
      localStorage.removeItem(`consumer-draft-${draftId}`);
    },
    onSuccess: (_data, draftId) => {
      void queryClient.invalidateQueries({
        queryKey: ['consumer', 'drafts', draftId],
      });
    },
  });
}
